// Vista ejecutiva para Autoridades (Secretario General, ProSec Admin, ProSec Judicial).
// Aparece en Perfil solo si el usuario es autoridad.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, CircularProgress, Chip, Alert, Divider, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired } from '../../api/endpoints';

const FIRMA_SERVICE_URL = process.env.REACT_APP_FIRMA_SERVICE_URL || 'http://127.0.0.1:5850';

const BR = '4px';
const PRIMARY = '#6a1b9a';

async function jf(path) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No token'); }
    const res = await fetch(nodeApiPath(path), { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

export default function VistaEjecutiva() {
    const [data, setData] = useState(null);
    const [pendientesFirma, setPendientesFirma] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [firmaDlg, setFirmaDlg] = useState({ open: false, disp: null, pin: '', saving: false, servicioOk: null });

    const cargar = async () => {
        try {
            const [r1, r2] = await Promise.all([
                jf('personal/vista-ejecutiva'),
                jf('personal/disposiciones-pendientes-firma'),
            ]);
            if (r1.ok) setData(r1); else setError(r1.mensaje);
            if (r2.ok) setPendientesFirma(r2.items || []);
        } catch (e) { setError(e.message); }
        setLoading(false);
    };

    useEffect(() => { cargar(); }, []);

    const abrirFirma = async (disp) => {
        let ok = false;
        try {
            const r = await fetch(`${FIRMA_SERVICE_URL}/status`, { signal: AbortSignal.timeout(2000) });
            // Que el cliente responda no alcanza: tiene que detectar el token.
            const d = r.ok ? await r.json().catch(() => ({})) : {};
            ok = (d.tokensDetectados ?? 0) > 0;
        } catch { ok = false; }
        setFirmaDlg({ open: true, disp, pin: '', saving: false, servicioOk: ok });
    };

    const firmar = async () => {
        if (!firmaDlg.pin) return;
        setFirmaDlg(p => ({ ...p, saving: true }));
        try {
            const d = firmaDlg.disp;
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            // 1. Generar PDF
            const det = await jf(`personal/disposiciones/${d.Id}`);
            if (!det.ok || !det.disposicion?.Contenido_HTML) throw new Error('Sin contenido');
            const pdfRes = await fetch(nodeApiPath('pdf/from-html'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ html: det.disposicion.Contenido_HTML, filename: `Disposicion_${d.Formato}`, orientation: 'portrait' }),
            });
            if (!pdfRes.ok) throw new Error('Error generando PDF');
            const pdfBlob = await pdfRes.blob();

            // 2. Firmar
            const fd = new FormData();
            fd.append('archivo', pdfBlob, `${d.Formato}.pdf`);
            const firmaRes = await fetch(`${FIRMA_SERVICE_URL}/firmar?pin=${encodeURIComponent(firmaDlg.pin)}`, { method: 'POST', body: fd });
            if (!firmaRes.ok) { const err = await firmaRes.json().catch(() => ({})); throw new Error(err.error || 'Error firmando'); }
            const firmado = await firmaRes.blob();

            // 3. Subir
            const fd2 = new FormData();
            fd2.append('file', firmado, `${d.Formato}_firmada.pdf`);
            const up = await fetch(nodeApiPath(`personal/disposiciones/${d.Id}/firmar`), {
                method: 'POST', body: fd2, headers: { Authorization: `Bearer ${token}` },
            });
            const r = await up.json();
            if (r.ok) { setFirmaDlg({ open: false, disp: null, pin: '', saving: false, servicioOk: null }); cargar(); }
            else throw new Error(r.mensaje || 'Error');
        } catch (e) { alert('Error: ' + e.message); }
        setFirmaDlg(p => ({ ...p, saving: false }));
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;
    if (error) return <Alert severity="info" sx={{ borderRadius: BR }}>{error}</Alert>;
    if (!data) return null;

    const { personal, pendienteAprobacion, pendientesEnWorkflow, mes, solicitudesAno, disposicionesAno } = data;
    const totalPersonal = (personal?.internos || 0) + (personal?.externos || 0);
    const horasTard = Math.floor((mes?.minutosTardanza || 0) / 60);
    const minTard = (mes?.minutosTardanza || 0) % 60;

    const cards = [
        { icon: <PeopleIcon />, label: 'Personal activo', value: totalPersonal, sub: `${personal?.internos || 0} internos · ${personal?.externos || 0} externos`, color: '#0277bd' },
        { icon: <PendingActionsIcon />, label: 'Pendientes mi aprobación', value: pendienteAprobacion || 0, sub: 'Requieren tu acción', color: '#e65100', destacado: pendienteAprobacion > 0 },
        { icon: <PendingActionsIcon />, label: 'Total en workflow', value: pendientesEnWorkflow || 0, sub: 'En todo el proceso', color: '#1565c0' },
        { icon: <BeachAccessIcon />, label: 'Licencias este mes', value: mes?.licencias || 0, sub: 'Solicitadas', color: '#00695c' },
        { icon: <AccessTimeIcon />, label: 'Tardanzas este mes', value: mes?.tardanzas || 0, sub: `${horasTard}h ${minTard}m acumulados`, color: '#e65100' },
        { icon: <EventBusyIcon />, label: 'Ausencias este mes', value: mes?.ausencias || 0, sub: 'Sin justificar', color: '#c62828' },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', ml: 'auto' }}>Actualizada ahora</Typography>
            </Box>

            {/* Cards principales */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5, mb: 3 }}>
                {cards.map(c => (
                    <Paper key={c.label} sx={{
                        p: 2, borderRadius: BR, borderLeft: `4px solid ${c.color}`,
                        bgcolor: c.destacado ? `${c.color}11` : '#fff',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ color: c.color, display: 'flex' }}>{c.icon}</Box>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>{c.label}</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: c.color, lineHeight: 1 }}>{c.value}</Typography>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>{c.sub}</Typography>
                    </Paper>
                ))}
            </Box>

            {/* Disposiciones pendientes de firma */}
            {pendientesFirma.length > 0 && (
                <Paper sx={{ p: 2, borderRadius: BR, mb: 3, borderLeft: '4px solid #2e7d32' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BorderColorIcon /> Disposiciones pendientes de mi firma ({pendientesFirma.length})
                    </Typography>
                    <Table size="small">
                        <TableHead><TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>N°</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Persona</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Concepto</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', textAlign: 'center' }}></TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {pendientesFirma.map(d => (
                                <TableRow key={d.Id} hover>
                                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: PRIMARY }}>{d.Formato}</TableCell>
                                    <TableCell><Chip label={d.Tipo} size="small" sx={{ fontSize: '0.65rem' }} /></TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{d.Apellido ? `${d.Apellido}, ${d.Nombre}` : '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', color: '#666', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.Concepto}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Button size="small" variant="contained" startIcon={<BorderColorIcon />} onClick={() => abrirFirma(d)}
                                            sx={{ bgcolor: '#2e7d32', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#1b5e20' } }}>
                                            Firmar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Solicitudes del año por tipo */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BeachAccessIcon fontSize="small" /> Solicitudes del año por tipo
                    </Typography>
                    {solicitudesAno?.length === 0 ? (
                        <Typography variant="caption" sx={{ color: '#999' }}>Sin datos</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {solicitudesAno?.map(s => (
                                <Box key={s.Tipo} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">{s.Tipo}</Typography>
                                    <Chip label={s.cnt} size="small" sx={{ fontWeight: 700 }} />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>

                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon fontSize="small" /> Disposiciones del año
                    </Typography>
                    {disposicionesAno?.length === 0 ? (
                        <Typography variant="caption" sx={{ color: '#999' }}>Ninguna generada aún</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {disposicionesAno?.map(d => (
                                <Box key={d.Estado} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">{d.Estado}</Typography>
                                    <Chip label={d.cnt} size="small" sx={{
                                        fontWeight: 700,
                                        bgcolor: d.Estado === 'EMITIDA' ? '#e8f5e9' : d.Estado === 'BORRADOR' ? '#fff3e0' : '#ffebee',
                                        color: d.Estado === 'EMITIDA' ? '#2e7d32' : d.Estado === 'BORRADOR' ? '#e65100' : '#c62828',
                                    }} />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Dialog Firmar */}
            <Dialog open={firmaDlg.open} onClose={() => setFirmaDlg({ open: false, disp: null, pin: '', saving: false, servicioOk: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BorderColorIcon /> Firmar {firmaDlg.disp?.Formato}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {firmaDlg.servicioOk === false ? (
                        <Alert severity="error" sx={{ borderRadius: BR }}>
                            No se detectó el servicio local de firma. Verificá que SIGIF Firma Local esté corriendo y el token USB conectado.
                        </Alert>
                    ) : (
                        <>
                            <Alert severity="info" sx={{ mb: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                                Insertá el token USB e ingresá el PIN para firmar digitalmente.
                            </Alert>
                            <TextField label="PIN del token" type="password" fullWidth autoFocus
                                value={firmaDlg.pin} onChange={e => setFirmaDlg(p => ({ ...p, pin: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') firmar(); }} sx={{ mt: 1 }} />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFirmaDlg({ open: false, disp: null, pin: '', saving: false, servicioOk: null })}
                        sx={{ textTransform: 'none' }} disabled={firmaDlg.saving}>Cancelar</Button>
                    {firmaDlg.servicioOk !== false && (
                        <Button onClick={firmar} variant="contained" disabled={firmaDlg.saving || !firmaDlg.pin}
                            startIcon={<BorderColorIcon />}
                            sx={{ bgcolor: '#2e7d32', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#1b5e20' } }}>
                            {firmaDlg.saving ? 'Firmando...' : 'Firmar'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
